// src/components/warehouse/WarehouseMapPicker.tsx
// Leaflet map that lets a user click to drop a pin.
// On pin drop: emits lat/lng + reverse-geocodes via Nominatim → emits address string.
// Matches the same Leaflet setup used in ScheduleMapView.tsx.

import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Loader2 } from "lucide-react";

export interface WarehousePin {
  lat: number;
  lng: number;
  address: string;
}

interface Props {
  /** Current pin — shown on first render if provided. */
  value?: { lat: number; lng: number } | null;
  /** Called whenever the user clicks a new location (after reverse-geocode completes). */
  onChange: (pin: WarehousePin) => void;
  /** Called while reverse-geocode is in flight. */
  onLoading?: (loading: boolean) => void;
}

// Default center: Tampa Bay area (matches ScheduleMapView default region)
const DEFAULT_CENTER: [number, number] = [27.9506, -82.4572];
const DEFAULT_ZOOM = 10;

function makeWarehouseIcon(): L.DivIcon {
  return L.divIcon({
    className: "",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -34],
    html: `<div style="
      width:32px;height:32px;
      background:#2563eb;
      border:2.5px solid white;
      border-radius:50% 50% 50% 0;
      transform:rotate(-45deg);
      box-shadow:0 2px 8px rgba(0,0,0,0.35);
      display:flex;align-items:center;justify-content:center;
    "><div style="transform:rotate(45deg);color:white;font-size:13px;line-height:1;">📦</div></div>`,
  });
}

async function reverseGeocode(lat: number, lng: number): Promise<string> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
      { headers: { "Accept-Language": "en" } }
    );
    if (!res.ok) return "";
    const data = await res.json();
    // Prefer display_name but trim it to a useful address
    const a = data.address ?? {};
    const parts = [
      a.house_number && a.road ? `${a.house_number} ${a.road}` : a.road,
      a.city || a.town || a.village || a.county,
      a.state,
      a.postcode,
    ].filter(Boolean);
    return parts.join(", ") || data.display_name || "";
  } catch {
    return "";
  }
}

export function WarehouseMapPicker({ value, onChange, onLoading }: Props) {
  const mapRef        = useRef<HTMLDivElement>(null);
  const mapInstance   = useRef<L.Map | null>(null);
  const markerRef     = useRef<L.Marker | null>(null);

  // Init map once
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    const map = L.map(mapRef.current, {
      zoomControl: false,
      attributionControl: false,
    }).setView(
      value ? [value.lat, value.lng] : DEFAULT_CENTER,
      value ? 14 : DEFAULT_ZOOM
    );

    L.control.zoom({ position: "topright" }).addTo(map);

    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      maxZoom: 19,
    }).addTo(map);

    // If a pin already exists, show it
    if (value) {
      markerRef.current = L.marker([value.lat, value.lng], { icon: makeWarehouseIcon() }).addTo(map);
    }

    // Click handler: drop/move pin, reverse geocode
    map.on("click", async (e: L.LeafletMouseEvent) => {
      const { lat, lng } = e.latlng;

      // Move existing marker or create new one
      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lng]);
      } else {
        markerRef.current = L.marker([lat, lng], { icon: makeWarehouseIcon() }).addTo(map);
      }

      onLoading?.(true);
      const address = await reverseGeocode(lat, lng);
      onLoading?.(false);

      onChange({ lat, lng, address });
    });

    mapInstance.current = map;

    return () => {
      map.remove();
      mapInstance.current = null;
      markerRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="rounded-md border overflow-hidden" style={{ height: 260 }}>
      <div ref={mapRef} className="w-full h-full" />
    </div>
  );
}

export { Loader2 };
